/**
 * Seven squares — did this client miss one session, or is it a pattern? — under a missed workout,
 * with the four weeks behind it as four tiles on the desk (owner, 26 September 2026: the tiles are
 * back from the one line of 24 September).
 *
 * Promoted out of `pages/tasks/ui/ClientWeekStrip.tsx` with the task card on 24 September 2026. It
 * owns its read now (`lib/useClientWeek`, one schedule read for the card on the desk) and its
 * wording, so a host passes the client and the day and nothing else. `model/clientWeek` decides
 * everything; this file is the drawing.
 *
 * ── Which week: the TASK's ────────────────────────────────────────────────────────────────────
 * The heading is "Ten tydzień" only when it is, and the span otherwise, settled before the read
 * lands so it never changes under the trainer (see `clientWeekAnchor`).
 *
 * ── A client with no dated sessions gets ONE sentence ────────────────────────────────────────
 * Seven empty squares say the client was given nothing this week; a self-paced plan is never given
 * days (self-paced-plan R15).
 */

import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { CheckIcon, CrossIcon } from '~/shared/ui/AuthIcons';
import { ElapsedMarkIcon } from '~/shared/ui/EmptyStateIcons';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useClientWeek } from '../lib/useClientWeek';
import { useHomeFormats } from '../lib/useHomeFormats';
import { clientWeekAnchor, type ClientWeekDay } from '../model/clientWeek';
import { AttendanceTiles } from './AttendanceTiles';
import blocks from './taskBlocks.module.css';
import styles from './weekStrip.module.css';

interface WeekStripProps {
  readonly customerId: number | null;
  /** The client's civil day the task is about, or `null` for "this week". */
  readonly taskDay: string | null;
  readonly todayIso: string;
  /** The quick card draws the squares shorter and no attendance tiles. */
  readonly compact?: boolean;
}

export function WeekStrip({ customerId, taskDay, todayIso, compact = false }: WeekStripProps) {
  const { t } = useTranslation();
  const { weekdayDay, shortDate } = useHomeFormats();
  const week = useClientWeek({ customerId, wanted: true, todayIso, taskDay });
  const gate = useSkeletonGate(week.pending);
  const anchor = clientWeekAnchor(todayIso, taskDay);
  const title =
    anchor === null || anchor.isCurrentWeek
      ? t('web:tasks.week.title')
      : t('web:tasks.week.range', {
          from: shortDate(anchor.startDay),
          to: shortDate(anchor.endDay),
        });

  if (gate.waiting) {
    return (
      <div className={styles.block} aria-busy="true">
        <h4 className={blocks.sectionTitle}>{title}</h4>
        <span className={`${String(styles.reserve)} ${compact ? String(styles.reserveCompact) : ''} ${gate.skeleton ? String(skel.bar) : ''}`} />
        {/* The attendance tiles' own boxes, held too: a strip that grew when the schedule landed
          * moved what stands under it (measured). */}
        {compact ? null : <AttendanceTiles weeks={null} gate={gate} />}
        <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
      </div>
    );
  }

  if (week.failed) {
    // The reserve's box stays and the words stand in it, so a failure moves nothing either.
    return (
      <div className={`${String(styles.block)} ${gate.reveal}`}>
        <h4 className={blocks.sectionTitle}>{title}</h4>
        <p className={`${String(styles.undated)} ${String(styles.reserve)} ${compact ? String(styles.reserveCompact) : ''}`}>
          {t('web:tasks.blocks.readFailed')}
        </p>
        {compact ? null : <AttendanceTiles weeks={null} gate={gate} />}
      </div>
    );
  }

  const shown = week.week;
  if (shown === null) return null;

  if (shown.kind === 'undated') {
    return (
      <div className={`${String(styles.block)} ${gate.reveal}`}>
        <h4 className={blocks.sectionTitle}>{title}</h4>
        <p className={styles.undated}>{t('web:tasks.week.undated')}</p>
      </div>
    );
  }

  return (
    <div className={`${String(styles.block)} ${gate.reveal}`}>
      <h4 className={blocks.sectionTitle}>{title}</h4>
      <div className={styles.strip}>
        {shown.days.map((day) => (
          <Square
            key={day.day}
            day={day}
            compact={compact}
            label={weekdayDay(day.day)}
          />
        ))}
      </div>
      {compact ? null : <AttendanceTiles weeks={shown.weeks} gate={gate} />}
    </div>
  );
}

function Square({
  day,
  compact,
  label,
}: {
  readonly day: ClientWeekDay;
  readonly compact: boolean;
  readonly label: string;
}) {
  const { t } = useTranslation();
  const verdict = verdictOf(day);

  return (
    <div
      className={`${String(styles.day)} ${compact ? String(styles.dayCompact) : ''} ${
        day.status === null ? String(styles.dayRest) : ''
      } ${day.isToday ? String(styles.dayToday) : ''} ${day.isTaskDay ? String(styles.dayTask) : ''}`}
    >
      <p className={styles.dayName}>{label}</p>
      <span className={`${String(styles.dayMark)} ${verdict.ink}`} aria-hidden="true">
        {verdict.mark}
      </span>
      {compact ? null : <p className={styles.dayTitle}>{day.title}</p>}
      <p className={styles.dayStatus}>
        {verdict.wordKey === null ? ' ' : t(verdict.wordKey)}
        {day.isTaskDay ? <span className={hidden.srOnly}> {t('web:tasks.week.taskDay')}</span> : null}
        {day.isToday ? <span className={hidden.srOnly}> {t('web:tasks.week.today')}</span> : null}
      </p>
    </div>
  );
}

interface Verdict {
  readonly mark: React.ReactNode;
  readonly ink: string;
  readonly wordKey: string | null;
}

function verdictOf(day: ClientWeekDay): Verdict {
  switch (day.status) {
    case 'completed':
      return { mark: <CheckIcon className={styles.dayMarkGlyph} />, ink: String(styles.markDone), wordKey: 'web:tasks.week.done' };
    case 'missed':
    case 'noShow':
      return { mark: <CrossIcon className={styles.dayMarkGlyph} />, ink: String(styles.markMissed), wordKey: 'web:tasks.week.missed' };
    case 'scheduled':
    case 'live':
      return { mark: <ElapsedMarkIcon className={styles.dayMarkGlyph} />, ink: String(styles.markPlanned), wordKey: 'web:tasks.week.planned' };
    default:
      return { mark: null, ink: '', wordKey: null };
  }
}
